import React, { useEffect, useState, Children, lazy, createElement, Component } from 'react';
import { CheckCircle2, ShieldCheck, FileText, ClipboardCheck, MessageSquare, FolderOpen, DollarSign, AlertTriangle, Building2, Star, Check, X, ChevronDown, MapPin, Clock, Flame } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const META_PIXEL_ID = '1012782631449952';
export function App() {
  useEffect(() => {
    // Meta Pixel base code + PageView
    const w = window as any;
    if (!w.fbq) {
      const n: any = w.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      const first = document.getElementsByTagName('script')[0];
      first?.parentNode?.insertBefore(script, first);
    }
    w.fbq('init', META_PIXEL_ID);
    w.fbq('track', 'PageView');
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-0">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brand-navy via-brand-navy to-brand-teal text-white py-10 md:py-16 px-4 overflow-hidden" data-id="element-1">
        <div className="max-w-3xl mx-auto" data-id="element-2">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="text-center" data-id="element-3">
            {/* Título principal, grande y llamativo */}
            <motion.h1 variants={fadeInUp} className="text-3xl md:text-5xl lg:text-6xl font-extrabold mb-5 md:mb-7 leading-[1.1] tracking-tight" data-id="element-4">
              Todo tu consultorio{' '}
              <span className="text-brand-gold" data-id="element-5">blindado y en regla</span>,
              resuelto
            </motion.h1>

            {/* Mockup al 70% de su tamaño, debajo del título */}
            <motion.div variants={fadeInUp} className="flex justify-center mb-5 md:mb-6" data-id="element-6">
              <img src="https://cdn.magicpatterns.com/uploads/uFM5JnzKAFxTDSP7RNpPme/Mockup_sin_fondo_Cirujano.png" alt="Megapack Blindaje Quirúrgico" fetchPriority="high" decoding="async" className="w-[70%] max-w-[260px] md:max-w-[320px] drop-shadow-2xl" data-id="element-7" />
            </motion.div>

            {/* 8 + 3 en cuadrados pequeños debajo del mockup */}
            <motion.div variants={fadeInUp} className="flex items-center justify-center gap-2 md:gap-3 mb-5 md:mb-7" data-id="element-8"></motion.div>

            {/* Subtítulo, debajo de los cuadrados */}
            <motion.p variants={fadeInUp} className="text-gray-200 text-sm md:text-base max-w-xl mx-auto mb-6 md:mb-8" data-id="element-9">
              Blindá tu consultorio en un solo fin de semana: consentimientos,
              historia clínica, partes quirúrgicos, protocolos y cobranza, todo
              listo para completar y aplicar. En 72 horas dejás de estar
              expuesto.
            </motion.p>

            <motion.div variants={fadeInUp} className="mb-4 md:mb-6 text-xs md:text-sm text-gray-300" data-id="element-10">
              PDF Digital · Acceso Inmediato · Para Cirujanos en Argentina
            </motion.div>
            <motion.a variants={fadeInUp} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} href="#pricing" className="inline-block bg-brand-gold hover:bg-yellow-500 text-brand-navy font-bold text-base md:text-lg px-6 md:px-8 py-3 md:py-4 rounded-lg transition-colors shadow-xl mb-4 md:mb-6" data-id="element-11">
              💥 QUIERO BLINDAR MI CONSULTORIO AHORA
            </motion.a>
            <motion.div variants={fadeInUp} className="text-xs md:text-sm text-gray-300 mb-3 md:mb-4" data-id="element-12">
              Garantía 7 días · Pago seguro · Mercado Pago
            </motion.div>
            <motion.div variants={fadeInUp} className="flex items-center justify-center gap-2 md:gap-4 text-xs md:text-sm" data-id="element-13">
              <span className="flex items-center gap-1" data-id="element-14">
                <Star className="w-3 h-3 md:w-4 md:h-4 fill-brand-gold text-brand-gold" data-id="element-15" />
                <span data-id="element-16">4.9 Rating</span>
              </span>
              <span data-id="element-17">·</span>
              <span data-id="element-18">Todo Argentina</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-10 md:py-16 px-4 bg-gray-50" data-id="element-19">
        <div className="max-w-6xl mx-auto" data-id="element-20">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-2xl md:text-4xl font-bold text-center mb-6 md:mb-12 text-brand-navy" data-id="element-21">
            LO QUE DICEN LOS COLEGAS
          </motion.h2>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-4 md:gap-8" data-id="element-22">
            <TestimonialCard quote="En un fin de semana ordené toda la documentación de mi consultorio. Ahora cada cirugía queda registrada como corresponde y duermo tranquilo. Antes lo dejaba para después y vivía con esa espina." author="Dr. Nombre" location="Ciudad, Cirujano General" data-id="element-23" />
            <TestimonialCard quote="Los consentimientos que usaba eran genéricos y no cubrían mis cirugías. Con las plantillas adapté los míos en una tarde. Es exactamente lo que un colega me dijo que le faltó cuando lo demandaron." author="Dr. Nombre" location="Ciudad, Cirujano Plástico" data-id="element-24" />
            <TestimonialCard quote="Lo que más me sirvió fue la planilla de honorarios y débitos. Descubrí dos obras sociales con las que estaba trabajando casi gratis. Lo recuperé en el primer mes." author="Dr. Nombre" location="Ciudad, Traumatólogo" data-id="element-25" />
          </motion.div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="py-10 md:py-16 px-4 bg-white" data-id="element-26">
        <div className="max-w-6xl mx-auto" data-id="element-27">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-28">
            <h2 className="text-2xl md:text-4xl font-bold text-center mb-2 md:mb-4 text-brand-navy" data-id="element-29">
              ¿TE PASA ESTO?
            </h2>
            <p className="text-center text-base md:text-xl text-gray-600 mb-6 md:mb-12" data-id="element-30">
              Si operás en Argentina, esto te suena familiar:
            </p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-2 gap-3 md:gap-6" data-id="element-31">
            <PainPoint title="Copiás consentimientos" description="Modelos genéricos que no cubren tu cirugía." data-id="element-32" />
            <PainPoint title="Documentás a las apuradas" description="Partes e historias con huecos que se pagan caro." data-id="element-33" />
            <PainPoint title="Espada de Damocles" description="Un papel mal hecho te puede arruinar." data-id="element-34" />
            <PainPoint title="Te demandan igual" description="El seguro paga, pero no devuelve el desgaste." data-id="element-35" />
            <PainPoint title="No sabés qué decir" description="Cuando algo se complica, lo improvisás." data-id="element-36" />
            <PainPoint title="Perdés plata" description="Débitos y demoras que nadie controla." data-id="element-37" />
            <PainPoint title="Consultorio changa" description="Sin seña, sin agenda, sin sistema." data-id="element-38" />
            <PainPoint title="Lo dejás para 'después'" description="Y llega la carta documento." data-id="element-39" />
          </motion.div>
          <motion.p initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center text-lg md:text-2xl font-bold text-brand-navy mt-8 md:mt-12" data-id="element-40">
            ¿Cuántas cirugías más vas a operar expuesto?
          </motion.p>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-10 md:py-16 px-4 bg-brand-navy text-white" data-id="element-41">
        <div className="max-w-4xl mx-auto" data-id="element-42">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-43">
            <h2 className="text-2xl md:text-4xl font-bold text-center mb-2 md:mb-4" data-id="element-44">
              ¿PARA QUIÉN ES?
            </h2>
            <p className="text-center text-base md:text-xl text-gray-300 mb-6 md:mb-12" data-id="element-45">
              Diseñado para vos si querés operar tranquilo y cobrar lo que vale
              tu trabajo
            </p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="space-y-3 md:space-y-6" data-id="element-46">
            <TargetCard title="Cirujanos que operan en privado" description="General, plástica, traumatología, ginecología y demás." data-id="element-47" />
            <TargetCard title="Tenés consultorio propio" description="Ves pacientes antes y después de operar y querés ordenarlo." data-id="element-48" />
            <TargetCard title="Te preocupa la mala praxis" description="Querés blindarte hoy, no el día que ya sea tarde." data-id="element-49" />
            <TargetCard title="Querés dejar de perder plata" description="Con obras sociales, débitos y ausentes." data-id="element-50" />
          </motion.div>
          <motion.p initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mt-6 md:mt-8 text-sm md:text-base text-gray-300" data-id="element-51">
            ¿No estás seguro si es para vos? Tenés 7 días de garantía →
          </motion.p>
        </div>
      </section>

      {/* System Overview */}
      <section className="py-10 md:py-16 px-4 bg-gradient-to-br from-brand-teal to-teal-700 text-white" data-id="element-52">
        <div className="max-w-6xl mx-auto text-center" data-id="element-53">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-54">
            <motion.div variants={fadeInUp} className="inline-block bg-brand-gold text-brand-navy px-4 md:px-6 py-1 md:py-2 rounded-full font-bold text-xs md:text-sm mb-4 md:mb-6" data-id="element-55">
              SISTEMA DE BLINDAJE N°1 PARA CIRUJANOS
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-bold mb-2 md:mb-4" data-id="element-56">
              Solucionesya.shop
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-base md:text-xl mb-6 md:mb-8 text-teal-100" data-id="element-57">
              Sistema completo · 8 Tomos + 3 Bonos · De la prevención a la
              defensa · Todo resuelto.
            </motion.p>
            <motion.div variants={fadeInUp} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-8 mb-6 md:mb-8" data-id="element-58">
              <p className="text-xl md:text-2xl font-bold mb-2 md:mb-4" data-id="element-59">
                +130 pág. · 50+ plantillas
              </p>
              <p className="text-sm md:text-lg text-teal-100" data-id="element-60">
                Consentimientos · Historia Clínica · Parte Quirúrgico ·
                Checklists OMS · Comunicación · Legajo · Honorarios · Defensa ·
                Consultorio · Ley 26.529
              </p>
            </motion.div>
            <motion.p variants={fadeInUp} className="text-base md:text-xl font-semibold mb-4 md:mb-6" data-id="element-61">
              Funciona desde hoy, en tu consultorio y tu quirófano.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-2 md:gap-3 text-xs md:text-sm" data-id="element-62">
              {['Consentimientos', 'Documentación', 'Quirófano', 'Complicaciones', 'Demanda', 'Cobranza', 'Agenda', 'Recupero', 'Mala praxis', 'Crecimiento'].map(item => <span key={item} className="bg-white/20 px-3 md:px-4 py-1 md:py-2 rounded-full" data-id="element-63">
                  {item}
                </span>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Content - 8 Tomos */}
      <section className="py-10 md:py-16 px-4 bg-white" data-id="element-64">
        <div className="max-w-6xl mx-auto" data-id="element-65">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-66">
            <h2 className="text-2xl md:text-4xl font-bold text-center mb-2 md:mb-4 text-brand-navy" data-id="element-67">
              CONTENIDO COMPLETO — LOS 8 TOMOS
            </h2>
            <p className="text-center text-base md:text-xl text-gray-600 mb-6 md:mb-12" data-id="element-68">
              Tu hoja de ruta para blindarte y ordenar tu consultorio
            </p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4" data-id="element-69">
            <TomoCard number="1" title="Consentimientos que te cubren de verdad" description="Modelos por tipo de cirugía, listos para completar." image="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=240&q=55&auto=format&fit=crop" icon={<FileText className="w-5 h-5" data-id="element-71" />} data-id="element-70" />
            <TomoCard number="2" title="Historia clínica y parte quirúrgico sin agujeros" description="Las dos planillas que el perito lee primero." image="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=240&q=55&auto=format&fit=crop" icon={<ClipboardCheck className="w-5 h-5" data-id="element-73" />} data-id="element-72" />
            <TomoCard number="3" title="Checklists de quirófano listos para imprimir" description="Listas pre y post quirúrgicas estándar OMS." image="https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=240&q=55&auto=format&fit=crop" icon={<CheckCircle2 className="w-5 h-5" data-id="element-75" />} data-id="element-74" />
            <TomoCard number="4" title="Qué decir cuando algo no sale como esperabas" description="Los guiones exactos para cada momento difícil." image="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=240&q=55&auto=format&fit=crop" icon={<MessageSquare className="w-5 h-5" data-id="element-77" />} data-id="element-76" />
            <TomoCard number="5" title="El Legajo Blindado" description="Archivá cada cirugía en una sola carpeta." image="https://images.unsplash.com/photo-1568667256549-094345857637?w=240&q=55&auto=format&fit=crop" icon={<FolderOpen className="w-5 h-5" data-id="element-79" />} data-id="element-78" />
            <TomoCard number="6" title="Honorarios y recupero sin perder plata" description="Sabé qué cobrás, qué te deben y qué te hace trabajar gratis." image="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=240&q=55&auto=format&fit=crop" icon={<DollarSign className="w-5 h-5" data-id="element-81" />} data-id="element-80" />
            <TomoCard number="7" title="Protocolo de las primeras 48 horas" description="Te llegó la carta documento: qué hacer, paso a paso." image="https://images.unsplash.com/photo-1504439468489-c8920d796a29?w=240&q=55&auto=format&fit=crop" icon={<AlertTriangle className="w-5 h-5" data-id="element-83" />} badge="Solo Megapack" data-id="element-82" />
            <TomoCard number="8" title="El consultorio que crece y cobra bien" description="Llená la agenda con pacientes que pagan al día." image="https://images.unsplash.com/photo-1497366216548-37526070297c?w=240&q=55&auto=format&fit=crop" icon={<Building2 className="w-5 h-5" data-id="element-85" />} badge="Solo Megapack" data-id="element-84" />
          </motion.div>
        </div>
      </section>

      {/* Bonos */}
      <section className="py-10 md:py-16 px-4 bg-gray-50" data-id="element-86">
        <div className="max-w-6xl mx-auto" data-id="element-87">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-88">
            <h2 className="text-2xl md:text-4xl font-bold text-center mb-2 md:mb-4 text-brand-navy" data-id="element-89">
              INCLUIDOS GRATIS — 3 BONOS ESTRATÉGICOS
            </h2>
            <p className="text-center text-base md:text-xl text-gray-600 mb-6 md:mb-12" data-id="element-90">
              Sin costo extra — incluidos en todas las ofertas
            </p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-3 gap-3 md:gap-6" data-id="element-91">
            <BonoCard number="1" title="Pack de mensajes listos para copiar y pegar" description="WhatsApp y mail para cada momento con tu paciente." image="https://images.unsplash.com/photo-1611605698335-8b1569810432?w=240&q=55&auto=format&fit=crop" icon={<MessageSquare className="w-5 h-5" data-id="element-93" />} data-id="element-92" />
            <BonoCard number="2" title="Calculadora de rentabilidad por cirugía y convenio" description="Cargás los números y ves qué cirugía te conviene." image="https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=240&q=55&auto=format&fit=crop" icon={<DollarSign className="w-5 h-5" data-id="element-95" />} data-id="element-94" />
            <BonoCard number="3" title="Semáforo de Exposición" description="En 5 minutos sabés qué tan expuesto estás a una demanda." image="https://images.unsplash.com/photo-1606857521015-7f9fcf423740?w=240&q=55&auto=format&fit=crop" icon={<ShieldCheck className="w-5 h-5" data-id="element-97" />} data-id="element-96" />
          </motion.div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-10 md:py-16 px-4 bg-white" data-id="element-98">
        <div className="max-w-5xl mx-auto" data-id="element-99">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-2xl md:text-4xl font-bold text-center mb-6 md:mb-12 text-brand-navy" data-id="element-100">
            LA REALIDAD: ¿Cuánto cuesta NO tenerlo?
          </motion.h2>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="overflow-x-auto rounded-xl shadow-sm border border-gray-100" data-id="element-101">
            <table className="w-full border-collapse min-w-[500px]" data-id="element-102">
              <thead data-id="element-103">
                <tr className="bg-brand-navy text-white" data-id="element-104">
                  <th className="p-3 md:p-4 text-left font-bold text-sm md:text-base" data-id="element-105">
                    Sin el sistema
                  </th>
                  <th className="p-3 md:p-4 text-left font-bold text-sm md:text-base" data-id="element-106">
                    Con el sistema
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-107">
                <ComparisonRow without="Consentimientos genéricos" with="Modelos específicos por cirugía" data-id="element-108" />
                <ComparisonRow without="Documentación con huecos" with="Registro completo que te defiende" alt data-id="element-109" />
                <ComparisonRow without="No sabés qué decir" with="Guiones listos para cada momento" data-id="element-110" />
                <ComparisonRow without="Día de la demanda: nada a mano" with="Legajo armado en una carpeta" alt data-id="element-111" />
                <ComparisonRow without="Plata que se va en débitos" with="Planillas que controlan cada cobro" data-id="element-112" />
                <ComparisonRow without="Consultorio desordenado" with="Agenda, seña y sistema funcionando" alt data-id="element-113" />
                <ComparisonRow without="Lo dejás para 'después'" with="En un fin de semana queda listo" data-id="element-114" />
                <ComparisonRow without="Vivís con la espina del juicio" with="Operás tranquilo y cubierto" alt data-id="element-115" />
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-10 md:py-16 px-4 bg-gradient-to-br from-brand-navy to-brand-teal" data-id="element-116">
        <div className="max-w-6xl mx-auto" data-id="element-117">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-118">
            <h2 className="text-2xl md:text-4xl font-bold text-center mb-2 md:mb-4 text-white" data-id="element-119">
              ELEGÍ TU BLINDAJE
            </h2>
            <p className="text-center text-base md:text-xl text-gray-200 mb-6 md:mb-12" data-id="element-120">
              Ambas opciones incluyen los 3 bonos estratégicos completos
            </p>
          </motion.div>
          <UrgencyBanner data-id="element-121" />
          <PlanSelector data-id="element-122" />
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-123">
            <p className="text-center text-sm md:text-base text-gray-200 mt-6 md:mt-8 px-4" data-id="element-124">
              La opción más elegida por cirujanos que quieren protección total y
              un consultorio que crece.
            </p>
            <p className="text-center text-xs md:text-sm text-gray-300 mt-2 md:mt-4" data-id="element-125">
              Pago seguro · Acceso en 5 min · Garantía 7 días · Mercado Pago
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Megapack Adds */}
      <section className="py-10 md:py-16 px-4 bg-white" data-id="element-126">
        <div className="max-w-4xl mx-auto" data-id="element-127">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-128">
            <h2 className="text-2xl md:text-4xl font-bold text-center mb-2 md:mb-4 text-brand-navy" data-id="element-129">
              ¿QUÉ AGREGA EL MEGAPACK?
            </h2>
            <p className="text-center text-base md:text-xl text-gray-600 mb-6 md:mb-12" data-id="element-130">
              Para el cirujano que quiere blindaje total: del peor día a la
              mejor versión de su consultorio.
            </p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-gray-50 rounded-xl p-4 md:p-8 mb-6 md:mb-8" data-id="element-131">
            <h3 className="text-xl md:text-2xl font-bold mb-4 text-brand-navy" data-id="element-132">
              El peor día de tu carrera, resuelto
            </h3>
            <div className="space-y-4" data-id="element-133">
              <div className="flex gap-3 md:gap-4" data-id="element-134">
                <X className="w-5 h-5 md:w-6 md:h-6 text-red-500 flex-shrink-0 mt-0.5 md:mt-1" data-id="element-135" />
                <div data-id="element-136">
                  <p className="font-semibold text-sm md:text-base text-gray-900" data-id="element-137">
                    Solo con el Kit:
                  </p>
                  <p className="text-xs md:text-sm text-gray-600" data-id="element-138">
                    te llega la carta documento y entrás en pánico, llamás a
                    cualquiera, cometés errores que se pagan caro.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 md:gap-4" data-id="element-139">
                <Check className="w-5 h-5 md:w-6 md:h-6 text-green-600 flex-shrink-0 mt-0.5 md:mt-1" data-id="element-140" />
                <div data-id="element-141">
                  <p className="font-semibold text-sm md:text-base text-gray-900" data-id="element-142">
                    Con el Megapack:
                  </p>
                  <p className="text-xs md:text-sm text-gray-600" data-id="element-143">
                    abrís el Protocolo de las primeras 48 horas y sabés
                    exactamente qué hacer, qué no firmar, a quién avisar y cómo
                    llegar fuerte a tu abogado.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-brand-navy text-white rounded-xl p-4 md:p-8" data-id="element-144">
            <h3 className="text-xl md:text-2xl font-bold mb-4 md:mb-6" data-id="element-145">
              EL MEGAPACK SUMA:
            </h3>
            <div className="space-y-4 md:space-y-6" data-id="element-146">
              <div className="flex gap-3 md:gap-4" data-id="element-147">
                <AlertTriangle className="w-6 h-6 md:w-8 md:h-8 text-brand-gold flex-shrink-0" data-id="element-148" />
                <div data-id="element-149">
                  <p className="font-bold text-base md:text-lg mb-1 md:mb-2" data-id="element-150">
                    T7 — Protocolo de las primeras 48 horas
                  </p>
                  <p className="text-xs md:text-sm text-gray-300" data-id="element-151">
                    El manual de emergencia para el día de la demanda. Qué
                    juntar, en qué orden y los errores que la mayoría comete en
                    pánico.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 md:gap-4" data-id="element-152">
                <Building2 className="w-6 h-6 md:w-8 md:h-8 text-brand-gold flex-shrink-0" data-id="element-153" />
                <div data-id="element-154">
                  <p className="font-bold text-base md:text-lg mb-1 md:mb-2" data-id="element-155">
                    T8 — El consultorio que crece y cobra bien
                  </p>
                  <p className="text-xs md:text-sm text-gray-300" data-id="element-156">
                    Política de seña, agenda llena, captación del paciente
                    particular y comunicación profesional dentro de las normas
                    del colegio médico.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-10 md:py-16 px-4 bg-brand-gold" data-id="element-157">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="max-w-4xl mx-auto text-center" data-id="element-158">
          <ShieldCheck className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 md:mb-6 text-brand-navy" data-id="element-159" />
          <h2 className="text-2xl md:text-4xl font-bold mb-2 md:mb-4 text-brand-navy" data-id="element-160">
            GARANTÍA TOTAL DE 7 DÍAS
          </h2>
          <p className="text-lg md:text-xl text-gray-800 mb-4 md:mb-6 font-medium" data-id="element-161">
            7 días para probarlo o te devolvemos el 100%
          </p>
          <p className="text-sm md:text-lg text-gray-800" data-id="element-162">
            Descargás los tomos, usás las plantillas en tu consultorio. Si no es
            lo que esperabas: sin preguntas, sin formularios, sin vueltas.
          </p>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="py-10 md:py-16 px-4 bg-white" data-id="element-163">
        <div className="max-w-3xl mx-auto" data-id="element-164">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-2xl md:text-4xl font-bold text-center mb-6 md:mb-12 text-brand-navy" data-id="element-165">
            PREGUNTAS FRECUENTES
          </motion.h2>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="space-y-3" data-id="element-166">
            <FAQItem question="¿Cómo recibo el material después de comprar?" answer="Acceso inmediato. Apenas se acredita el pago, te llegan todos los PDF a tu mail para descargar y usar al instante." data-id="element-167" />
            <FAQItem question="¿Las plantillas sirven para mi especialidad?" answer="Sí. Los consentimientos y módulos están pensados como base profesional adaptable a cualquier especialidad quirúrgica: cargás tus datos y los ajustás a tu práctica." data-id="element-168" />
            <FAQItem question="¿Esto reemplaza a mi abogado o a mi seguro de mala praxis?" answer="No, y no busca hacerlo. Es la base organizativa y documental que te deja cubierto y ordenado para que tu abogado y tu seguro trabajen mejor. Te recomendamos validar los modelos con ellos." data-id="element-169" />
            <FAQItem question="¿Los 3 bonos están incluidos en las dos ofertas?" answer="Sí, los tres bonos vienen completos tanto en el Kit como en el Megapack." data-id="element-170" />
            <FAQItem question="¿Está alineado a la ley argentina?" answer="Sí. Todo el material está pensado para el marco argentino: Ley 26.529 de derechos del paciente, estándar de seguridad de la OMS y la realidad de las obras sociales y prepagas locales." data-id="element-171" />
            <FAQItem question="¿En cuánto tiempo lo puedo implementar?" answer="El primer consentimiento específico lo tenés andando el mismo día. Todo el sistema funcionando en tu consultorio, en un fin de semana." data-id="element-172" />
            <FAQItem question="¿Qué diferencia tiene con buscar modelos gratis en internet?" answer="Los modelos sueltos protegen a la clínica o no cubren tu cirugía. Esto es un sistema integral, específico del cirujano argentino y listo para usar, sin armar nada de cero." data-id="element-173" />
            <FAQItem question="¿Por qué elegir el Megapack y no solo el Kit?" answer="Porque suma las dos cosas que más te importan: el protocolo para el día de una demanda y el sistema para que tu consultorio cobre mejor. Protección total y más plata, por una diferencia mínima." data-id="element-174" />
            <FAQItem question="¿Tiene garantía real?" answer="Sí, 7 días reales. Si no te sirve, te devolvemos el 100%." data-id="element-175" />
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-10 md:py-16 px-4 bg-gradient-to-br from-brand-navy to-brand-teal text-white" data-id="element-176">
        <div className="max-w-4xl mx-auto text-center" data-id="element-177">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-2xl md:text-4xl font-bold mb-6 md:mb-12" data-id="element-178">
            No sigas operando expuesto
          </motion.h2>
          <UrgencyBanner data-id="element-179" />
          <PlanSelector data-id="element-180" />
          <motion.p initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-xs md:text-sm text-gray-300 mt-6 md:mt-8" data-id="element-181">
            Pago seguro · Acceso en 5 min · Garantía 7 días · Mercado Pago
          </motion.p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-brand-navy text-white py-8 md:py-12 px-4" data-id="element-182">
        <div className="max-w-6xl mx-auto" data-id="element-183">
          <div className="text-center mb-6 md:mb-8" data-id="element-184">
            <h3 className="text-lg md:text-2xl font-bold mb-2" data-id="element-185">
              🩺 MEGAPACK BLINDAJE QUIRÚRGICO
            </h3>
            <p className="text-xs md:text-sm text-gray-300 mb-4 max-w-2xl mx-auto" data-id="element-186">
              El sistema de blindaje N°1 para cirujanos en Argentina. Porque
              operar bien no debería dejarte expuesto a una demanda ni a perder
              plata con las obras sociales.
            </p>
            <div className="flex flex-wrap justify-center gap-2 md:gap-4 text-xs md:text-sm text-gray-400" data-id="element-187">
              <a href="mailto:contacto@solucionesya.shop" className="hover:text-white transition-colors" data-id="element-188">
                contacto@solucionesya.shop
              </a>
              <span className="hidden md:inline" data-id="element-189">·</span>
              <a href="#" className="hover:text-white transition-colors" data-id="element-190">
                @solucionesya.ar
              </a>
              <span className="hidden md:inline" data-id="element-191">·</span>
              <a href="#" className="hover:text-white transition-colors" data-id="element-192">
                solucionesya.shop
              </a>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-6 md:pt-8 text-center text-xs text-gray-400" data-id="element-193">
            <p className="mb-3 md:mb-4" data-id="element-194">
              © Solucionesya.shop 2026 — Todos los derechos reservados.
            </p>
            <p className="leading-relaxed max-w-4xl mx-auto opacity-70" data-id="element-195">
              Aviso: el material de este producto es una base profesional de
              gestión y organización para adaptar a tu práctica. No constituye
              asesoramiento legal ni reemplaza a tu abogado ni a tu seguro de
              mala praxis. Los modelos y plazos deben validarse con un
              profesional del derecho.
            </p>
          </div>
        </div>
      </footer>

      {/* Pop-up de compra verificada (cada 40-60 s) */}
      <PurchasePopup data-id="element-196" />
    </div>;
}
// Component definitions
const BUYER_NAMES = ['Dr. Martín Fernández', 'Dra. Sofía Gómez', 'Dr. Lucas Rodríguez', 'Dra. Valentina Pérez', 'Dr. Juan Martínez', 'Dra. Camila López', 'Dr. Mateo Sánchez', 'Dra. Julieta Romero', 'Dr. Santiago Díaz', 'Dra. Florencia Torres', 'Dr. Nicolás Álvarez', 'Dra. Agustina Ruiz', 'Dr. Federico Suárez', 'Dra. Carla Benítez', 'Dr. Gonzalo Acosta', 'Dra. Micaela Herrera'];
const ARG_CITIES = ['CABA', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'Mar del Plata', 'San Miguel de Tucumán', 'Salta', 'Santa Fe', 'Neuquén', 'Bahía Blanca', 'Resistencia', 'Posadas', 'San Juan'];
const OFFER_NAMES = ['Kit Blindaje Quirúrgico', 'Megapack Blindaje Quirúrgico'];
function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
type PurchaseNotice = {
  id: number;
  name: string;
  city: string;
  offer: string;
};
function PurchasePopup() {
  const [notice, setNotice] = useState<PurchaseNotice | null>(null);
  useEffect(() => {
    let visibleTimer: ReturnType<typeof setTimeout>;
    let nextTimer: ReturnType<typeof setTimeout>;
    const show = () => {
      setNotice({
        id: Date.now(),
        name: pick(BUYER_NAMES),
        city: pick(ARG_CITIES),
        offer: pick(OFFER_NAMES)
      });
      // Visible ~5.5 s, then hide and schedule the next one in 40-60 s.
      visibleTimer = setTimeout(() => {
        setNotice(null);
        const nextDelay = 40000 + Math.random() * 20000;
        nextTimer = setTimeout(show, nextDelay);
      }, 5500);
    };
    // First popup after a short delay.
    const startTimer = setTimeout(show, 6000);
    return () => {
      clearTimeout(startTimer);
      clearTimeout(visibleTimer);
      clearTimeout(nextTimer);
    };
  }, []);
  return <div className="fixed bottom-4 left-4 z-50 pointer-events-none" data-id="element-197">
      <AnimatePresence data-id="element-198">
        {notice && <motion.div key={notice.id} initial={{
        opacity: 0,
        y: 24,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 24,
        scale: 0.95
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} className="pointer-events-auto relative flex items-start gap-3 bg-white rounded-xl shadow-2xl border border-gray-100 p-3 pr-8 max-w-[300px]" data-id="element-199">
            <button type="button" onClick={() => setNotice(null)} aria-label="Cerrar notificación" className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-200">
              <X className="w-4 h-4" data-id="element-201" />
            </button>
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center" data-id="element-202">
              <CheckCircle2 className="w-6 h-6 text-green-600" data-id="element-203" />
            </div>
            <div className="min-w-0" data-id="element-204">
              <div className="flex items-center gap-1.5 mb-0.5" data-id="element-205">
                <span className="text-[10px] font-bold text-green-700 uppercase tracking-wide" data-id="element-206">
                  Compra verificada
                </span>
              </div>
              <p className="text-sm font-bold text-brand-navy leading-tight" data-id="element-207">
                {notice.name}
              </p>
              <p className="text-xs text-gray-600 flex items-center gap-1 leading-tight" data-id="element-208">
                <MapPin className="w-3 h-3 text-brand-teal flex-shrink-0" data-id="element-209" />
                {notice.city}, Argentina
              </p>
              <p className="text-xs text-gray-700 mt-1 leading-tight" data-id="element-210">
                Compró ahora{' '}
                <span className="font-semibold text-brand-teal" data-id="element-211">
                  {notice.offer}
                </span>
              </p>
              <p className="text-[10px] text-gray-400 mt-1" data-id="element-212">Hace un momento</p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function TestimonialCard({
  quote,
  author,
  location
}: {
  quote: string;
  author: string;
  location: string;
}) {
  return <motion.div variants={fadeInUp} whileHover={{
    y: -5,
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
  }} className="bg-white rounded-xl p-4 md:p-6 shadow-md border border-gray-100 transition-all" data-id="element-213">
      <p className="text-sm md:text-base text-gray-700 mb-3 md:mb-4 italic" data-id="element-214">
        "{quote}"
      </p>
      <div className="text-xs md:text-sm" data-id="element-215">
        <p className="font-bold text-brand-navy" data-id="element-216">{author}</p>
        <p className="text-gray-600" data-id="element-217">{location}</p>
      </div>
    </motion.div>;
}
function PainPoint({
  title,
  description
}: {
  title: string;
  description: string;
}) {
  return <motion.div variants={fadeInUp} whileHover={{
    scale: 1.02
  }} className="bg-red-50 border-l-4 border-red-500 p-3 md:p-4 rounded-r-lg" data-id="element-218">
      <h3 className="font-bold text-sm md:text-base text-red-900 mb-1" data-id="element-219">
        {title}
      </h3>
      <p className="text-xs md:text-sm text-red-800 leading-tight" data-id="element-220">
        {description}
      </p>
    </motion.div>;
}
function TargetCard({
  title,
  description
}: {
  title: string;
  description: string;
}) {
  return <motion.div variants={fadeInUp} whileHover={{
    x: 5,
    backgroundColor: 'rgba(255,255,255,0.15)'
  }} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 md:p-6 flex items-start gap-3 md:gap-4 transition-colors" data-id="element-221">
      <Check className="w-5 h-5 md:w-6 md:h-6 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-222" />
      <div data-id="element-223">
        <h3 className="font-bold text-base md:text-lg mb-1" data-id="element-224">{title}</h3>
        <p className="text-sm md:text-base text-gray-300" data-id="element-225">{description}</p>
      </div>
    </motion.div>;
}
function hideBrokenImage(e: React.SyntheticEvent<HTMLImageElement>) {
  // If a representative photo fails to load, hide it so the
  // tinted icon tile underneath stays visible.
  e.currentTarget.style.display = 'none';
}
function TomoCard({
  number,
  title,
  description,
  icon,
  image,
  badge
}: {
  number: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  image: string;
  badge?: string;
}) {
  return <motion.div variants={fadeInUp} whileHover={{
    y: -4,
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.12)'
  }} whileTap={{
    scale: 0.99
  }} className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col transition-all cursor-pointer" data-id="element-226">
      {/* Representative image — lazy-loaded, falls back to the icon tile */}
      <div className="relative h-20 md:h-24 bg-brand-teal/10 flex items-center justify-center text-brand-teal" data-id="element-227">
        {icon}
        <img src={image} alt={title} loading="lazy" decoding="async" onError={hideBrokenImage} className="absolute inset-0 w-full h-full object-cover" data-id="element-228" />
        <span className="absolute top-1.5 left-1.5 bg-brand-navy text-white font-bold px-1.5 py-0.5 rounded text-[10px] md:text-xs shadow" data-id="element-229">
          T{number}
        </span>
        {badge && <span className="absolute top-1.5 right-1.5 bg-brand-gold text-brand-navy font-bold px-1.5 py-0.5 rounded text-[9px] md:text-[10px] shadow" data-id="element-230">
            ⭐ {badge}
          </span>}
      </div>
      <div className="p-2.5 md:p-3 flex-1" data-id="element-231">
        <h3 className="font-bold text-xs md:text-sm text-brand-navy mb-0.5 md:mb-1 leading-tight" data-id="element-232">
          {title}
        </h3>
        <p className="text-[11px] md:text-xs text-gray-600 leading-snug" data-id="element-233">
          {description}
        </p>
      </div>
    </motion.div>;
}
function BonoCard({
  number,
  title,
  description,
  icon,
  image
}: {
  number: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  image: string;
}) {
  return <motion.div variants={fadeInUp} whileHover={{
    y: -4,
    boxShadow: '0 10px 25px -5px rgba(217, 165, 52, 0.3)'
  }} className="bg-white rounded-xl overflow-hidden shadow-md border border-brand-gold/30 hover:border-brand-gold transition-all flex flex-col" data-id="element-234">
      <div className="relative h-20 md:h-24 bg-brand-gold/10 flex items-center justify-center text-brand-gold" data-id="element-235">
        {icon}
        <img src={image} alt={title} loading="lazy" decoding="async" onError={hideBrokenImage} className="absolute inset-0 w-full h-full object-cover" data-id="element-236" />
        <span className="absolute top-1.5 left-1.5 bg-brand-gold text-brand-navy font-bold w-6 h-6 rounded-full flex items-center justify-center text-[10px] md:text-xs shadow" data-id="element-237">
          B{number}
        </span>
        <span className="absolute bottom-1.5 right-1.5 bg-green-600 text-white font-bold px-1.5 py-0.5 rounded text-[9px] md:text-[10px] shadow" data-id="element-238">
          GRATIS
        </span>
      </div>
      <div className="p-2.5 md:p-3 flex-1" data-id="element-239">
        <h3 className="font-bold text-xs md:text-sm text-brand-navy mb-0.5 md:mb-1 leading-tight" data-id="element-240">
          {title}
        </h3>
        <p className="text-[11px] md:text-xs text-gray-600 leading-snug" data-id="element-241">
          {description}
        </p>
      </div>
    </motion.div>;
}
function ComparisonRow({
  without,
  with: withSystem,
  alt
}: {
  without: string;
  with: string;
  alt?: boolean;
}) {
  return <tr className={alt ? 'bg-gray-50' : 'bg-white'} data-id="element-242">
      <td className="p-3 md:p-4 border-b border-gray-100" data-id="element-243">
        <div className="flex items-start gap-2" data-id="element-244">
          <X className="w-4 h-4 md:w-5 md:h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-245" />
          <span className="text-xs md:text-sm text-gray-700" data-id="element-246">{without}</span>
        </div>
      </td>
      <td className="p-3 md:p-4 border-b border-gray-100" data-id="element-247">
        <div className="flex items-start gap-2" data-id="element-248">
          <Check className="w-4 h-4 md:w-5 md:h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-249" />
          <span className="text-xs md:text-sm text-gray-700 font-medium" data-id="element-250">
            {withSystem}
          </span>
        </div>
      </td>
    </tr>;
}
type PlanId = 'kit' | 'megapack';
const PLANS: Record<PlanId, {
  id: PlanId;
  name: string;
  price: string;
  tomos: string;
  checkoutUrl: string;
  featured?: boolean;
}> = {
  kit: {
    id: 'kit',
    name: 'KIT BLINDAJE QUIRÚRGICO',
    price: '$25.990',
    tomos: '6 tomos + 3 bonos',
    checkoutUrl: 'https://checkout.solucionesya.shop/cart/49707106205944:1'
  },
  megapack: {
    id: 'megapack',
    name: 'MEGAPACK BLINDAJE QUIRÚRGICO',
    price: '$35.990',
    tomos: '8 tomos + 3 bonos',
    checkoutUrl: 'https://checkout.solucionesya.shop/cart/49707118166264:1',
    featured: true
  }
};
function UrgencyBanner() {
  const [secondsLeft, setSecondsLeft] = useState(30 * 60); // 30 min
  const [reserved, setReserved] = useState(77); // barra desde el 77%
  useEffect(() => {
    const tick = setInterval(() => {
      setSecondsLeft(s => s > 0 ? s - 1 : 0);
    }, 1000);
    // La barra "carga" lentamente desde el 77% para reforzar la urgencia.
    const creep = setInterval(() => {
      setReserved(r => r < 94 ? r + 1 : r);
    }, 18000);
    return () => {
      clearInterval(tick);
      clearInterval(creep);
    };
  }, []);
  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.4
  }} className="max-w-2xl mx-auto mb-4 md:mb-6 rounded-xl overflow-hidden shadow-2xl ring-2 ring-yellow-300/70" data-id="element-251">
      <div className="bg-gradient-to-r from-red-600 via-red-500 to-orange-500 text-white p-3 md:p-4" data-id="element-252">
        <div className="flex items-center justify-between gap-3" data-id="element-253">
          {/* Mensaje de urgencia */}
          <div className="flex items-start gap-2 min-w-0" data-id="element-254">
            <motion.div animate={{
            scale: [1, 1.18, 1]
          }} transition={{
            repeat: Infinity,
            duration: 1.1
          }} className="flex-shrink-0 mt-0.5" data-id="element-255">
              <Flame className="w-5 h-5 md:w-6 md:h-6 text-yellow-300" data-id="element-256" />
            </motion.div>
            <div className="min-w-0" data-id="element-257">
              <p className="font-extrabold text-sm md:text-base leading-tight uppercase tracking-tight" data-id="element-258">
                ¡Últimos cupos en oferta!
              </p>
              <p className="text-[11px] md:text-sm leading-snug text-yellow-50" data-id="element-259">
                Cuando se agoten, el precio sube{' '}
                <span className="font-extrabold text-yellow-300" data-id="element-260">$10.000</span>{' '}
                y no vuelve a bajar.
              </p>
            </div>
          </div>
          {/* Timer */}
          <div className="flex-shrink-0 text-center" data-id="element-261">
            <div className="flex items-center gap-1 bg-black/25 rounded-lg px-2 md:px-3 py-1 md:py-1.5" data-id="element-262">
              <Clock className="w-3.5 h-3.5 md:w-4 md:h-4 text-yellow-300" data-id="element-263" />
              <span className="font-mono font-extrabold text-base md:text-xl tabular-nums tracking-wider" data-id="element-264">
                {mm}:{ss}
              </span>
            </div>
            <p className="text-[9px] md:text-[10px] mt-0.5 text-yellow-50 uppercase tracking-wide" data-id="element-265">
              Termina la oferta
            </p>
          </div>
        </div>
        {/* Barra de cupos desde el 77% */}
        <div className="mt-2.5 md:mt-3" data-id="element-266">
          <div className="flex items-center justify-between text-[10px] md:text-xs font-semibold mb-1" data-id="element-267">
            <span className="text-yellow-50" data-id="element-268">Cupos reservados</span>
            <span className="text-yellow-300" data-id="element-269">{reserved}%</span>
          </div>
          <div className="w-full h-2.5 md:h-3 bg-black/30 rounded-full overflow-hidden" data-id="element-270">
            <motion.div initial={{
            width: '0%'
          }} whileInView={{
            width: `${reserved}%`
          }} viewport={{
            once: true
          }} animate={{
            width: `${reserved}%`
          }} transition={{
            duration: 1.2,
            ease: 'easeOut'
          }} className="h-full bg-gradient-to-r from-yellow-300 to-yellow-400 rounded-full relative" data-id="element-271">
              <span className="absolute inset-0 bg-white/30 animate-pulse" data-id="element-272" />
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>;
}
function PlanSelector() {
  // Megapack (higher price) is pre-selected by default.
  const [selected, setSelected] = useState<PlanId>('megapack');
  const activePlan = PLANS[selected];
  return <motion.div initial="hidden" whileInView="visible" viewport={{
    once: true
  }} variants={fadeInUp} className="max-w-2xl mx-auto" data-id="element-273">
      {/* Both options stacked as full-width horizontal rows */}
      <div className="flex flex-col gap-3 md:gap-4" data-id="element-274">
        {(Object.values(PLANS) as (typeof PLANS)[PlanId][]).map(plan => {
        const isSelected = selected === plan.id;
        return <motion.button key={plan.id} type="button" onClick={() => setSelected(plan.id)} whileTap={{
          scale: 0.99
        }} aria-pressed={isSelected} className={`relative w-full flex items-center gap-3 md:gap-4 text-left rounded-xl p-3 md:p-4 transition-all focus:outline-none ${isSelected ? 'bg-white ring-4 ring-brand-gold shadow-2xl' : 'bg-white/10 ring-1 ring-white/30 hover:ring-white/60'}`} data-id="element-275">
              {/* Radio indicator */}
              <span className={`flex-shrink-0 w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-brand-gold bg-brand-gold' : 'border-white/50'}`} data-id="element-276">
                {isSelected && <Check className="w-3 h-3 md:w-4 md:h-4 text-brand-navy" strokeWidth={3} data-id="element-277" />}
              </span>
              {/* Plan name + detail */}
              <div className="flex-1 min-w-0" data-id="element-278">
                <div className="flex items-center gap-2 flex-wrap" data-id="element-279">
                  <p className={`font-bold text-xs md:text-base leading-tight ${isSelected ? 'text-brand-navy' : 'text-white'}`} data-id="element-280">
                    {plan.name}
                  </p>
                  {plan.featured && <span className="bg-brand-gold text-brand-navy px-2 py-0.5 rounded-full text-[9px] md:text-[10px] font-bold whitespace-nowrap shadow" data-id="element-281">
                      ⭐ MÁS ELEGIDO
                    </span>}
                </div>
                <p className={`text-[10px] md:text-xs mt-0.5 ${isSelected ? 'text-gray-600' : 'text-gray-300'}`} data-id="element-282">
                  {plan.tomos}
                </p>
              </div>
              {/* Price */}
              <p className={`flex-shrink-0 text-xl md:text-3xl font-extrabold ${isSelected ? 'text-brand-navy' : 'text-white'}`} data-id="element-283">
                {plan.price}
              </p>
            </motion.button>;
      })}
      </div>

      {/* Shared buy button — redirects to the selected plan's checkout */}
      <motion.a key={activePlan.id} href={activePlan.checkoutUrl} whileHover={{
      scale: 1.03
    }} whileTap={{
      scale: 0.97
    }} initial={{
      opacity: 0.6
    }} animate={{
      opacity: 1
    }} className="mt-4 md:mt-5 block w-full text-center bg-brand-gold hover:bg-yellow-500 text-brand-navy font-bold px-4 md:px-8 py-3 md:py-4 rounded-lg transition-colors text-sm md:text-lg shadow-xl" data-id="element-284">
        💥 COMPRAR AHORA — {activePlan.price}
      </motion.a>
    </motion.div>;
}
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <motion.div variants={fadeInUp} className="bg-gray-50 rounded-lg overflow-hidden border border-gray-100" data-id="element-285">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-4 text-left focus:outline-none" data-id="element-286">
        <h3 className="font-bold text-sm md:text-base text-brand-navy pr-4" data-id="element-287">
          {question}
        </h3>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} data-id="element-288">
          <ChevronDown className="w-5 h-5 text-brand-navy flex-shrink-0" data-id="element-289" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-290">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} data-id="element-291">
            <div className="p-4 pt-0 text-xs md:text-sm text-gray-600 border-t border-gray-100" data-id="element-292">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}